import { ThunkAction } from 'redux-thunk';
import { AnyAction } from 'redux';
import { HttpClient } from 'support/api/api-types';
import { Location } from 'history';

export type Provider = typeof Providers[keyof typeof Providers];

export type TileSize = 'sm' | 'md' | 'lg';
export type Platform =
  | 'unknown'
  | 'samsung-orsay'
  | 'samsung-tizen'
  | 'vizio'
  | 'tcl'
  | 'lg-webos'
  | 'lg-netcast'
  | 'sony'
  | 'sagem'
  | 'netgem'
  | 'tivo'
  | 'ps3'
  | 'ps4'
  | 'xboxone'
  | 'windows-desktop'
  | 'androidtv'
  | 'firetv';

export type PageType = 'sport' | 'league' | 'market' | 'show' | 'custom' | 'tagrow' | null;

export interface TrackInteriorPageParams {
  content: string;
  pageType?: PageType;
  historyStack: Location[];
}
/** Paramount+ authentication details for video content */
export interface ParamountPlusAuth {
  essentials?: boolean;
  premium?: boolean;
}

/** Promise action for custom promise middleware */
export interface PromiseAction<T = unknown> {
  type: string;
  promise: Promise<T> | ((httpClient: HttpClient) => Promise<T>);
  meta?: unknown;
}

/** Combined dispatch type supporting thunks and promise middleware */
export interface AppDispatch<State = unknown> {
  // Promise action overload - must come first
  <T>(action: PromiseAction<T>): Promise<T>;
  // Thunk overload
  <R>(thunk: ThunkAction<R, State, unknown, AnyAction>): R;
  // Plain action overload
  <A extends AnyAction>(action: A): A;
}

export interface ChannelInfo {
  resourceId: string;
  name: string;
  logoSmallUrl: string;
  logoMediumUrl: string;
  logoLargeUrl: string;
  logoFixedSmallUrl: string;
  logoFixedMediumUrl: string;
  logoFixedLargeUrl: string;
}

export type ChannelMap = Record<string, ChannelInfo>;

export type ChannelKey =
  | 'CBSE'
  | 'CBSSN'
  | 'PARAMOUNTPLUS'
  | 'CBSSHQ'
  | 'CBSSGOLAZO'
  | 'CBSSPORTS'
  | 'NFLCHANNEL'
  | 'CBSNEWS'
  | 'CBSNEWS247'
  | 'CBSNEWSBALTIMORE'
  | 'CBSNEWSBOSTON'
  | 'CBSNEWSCHICAGO'
  | 'CBSNEWSCOLORADO'
  | 'CBSNEWSDETROIT'
  | 'CBSNEWSLOSANGELES'
  | 'CBSNEWSMIAMI'
  | 'CBSNEWSMINNESOTA'
  | 'CBSNEWSNEWYORK'
  | 'CBSNEWSPHILADELPHIA'
  | 'CBSNEWSPITTSBURGH'
  | 'CBSNEWSSACRAMENTO'
  | 'CBSNEWSSANFRANCISCO'
  | 'CBSNEWSTEXAS'
  | 'CBSCHAMPIONSLEAGUE'
  | 'CARCHASE'
  | 'ENTERTAINMENTTONIGHT'
  | 'INSIDEEDITION'
  | 'CBS48HOURS'
  | 'CBS60MINUTES';

/** Strictly typed channel map with known keys */
export type Network = Record<ChannelKey, ChannelInfo>;

export interface AuthenticationDetails {
  adobe: null;
  paramountplus: ParamountPlusAuth | null;
}
export const Providers = {
  ADOBE: 'adobe',
  PARAMOUNT_PLUS: 'paramountplus',
} as const;
/** Raw API response for autoplay video object (before parsing by parseVideo) */
export interface AutoPlayObjectType {
  promo: PromoType | null;
  featured: boolean;
  id: string;
  dmaStation?: string;
  video: {
    orientation?: string;
    sources: {
      dash: {
        url: string | null;
        urlNoAd: string | null;
      };
      hls: {
        url: string | null;
        urlNoAd: string | null;
      };
    };
    identifiers: {
      id: string;
      slug: string;
      viacomContentId: string | null;
      trackingId: string;
    };
    relatedObject: null;
    about: {
      duration?: number;
      images?: {
        baseImage16X9: string | null;
        baseImage16X5: string | null;
      };
      prefix: string | null;
      description?: string;
      publishedOn?: number;
      segmentTitle: string | null;
      shortTitle?: string;
      title: string;
    };
    flags: {
      suppressPreroll?: boolean;
      isPlaybackBrowser?: boolean;
      isAppDrawer?: boolean;
      isAppScoreboard?: boolean;
      useDai?: boolean;
    };
    network: ChannelKey | null;
    analytics?: {
      neilsonGenre: string | null;
    };
    schedule: {
      preEndDate: number | null;
      preStartDate: number | null;
      videoStartDate: number | null;
      postEndDate: number | null;
      videoEndDate: number | null;
      apiStartDate: number | null;
      apiEndDate: number | null;
      postStartDate: number | null;
    };
    ads: {
      dai?: {
        daiApiKey: string | null;
        daiIu: string | null;
        daiCsid: string | null;
        daiApto: number | null;
        daiOv: string | null;
        daiAssetKey: string | null;
        daiOt: string | null;
      };
      vtag: string | null;
    };
    location?: boolean;
    playback: 'openHLS' | 'syncbak' | 'hls_encryption' | 'fullDRM' | string;
    event: {
      preEvent: string | null;
      pregame: string | null;
      postgame: string | null;
      frequency?: number;
      postEvent: string | null;
      status: 'active' | 'inactive' | string;
    };
    properties: {
      gameId: string | null;
      playlist: string | null;
      programInfo?: {
        source: string;
        frequency: number;
      };
      gameStartTime: string | null;
      league: string | null;
      eventType: string | null;
      type: 'vod' | 'live' | 'live-linear' | string;
      tags: string[] | null;
      tagSlugs?: string[];
    };
    authenticationDetails?: AuthenticationDetails | null;
    authentication: Provider[];
    series?: {
      id: string | null;
      season: string | null;
      episode: string | null;
    };
  };
  order: number;
  deeplink: string | null;
  dma: string | null;
  dmaGroup: string[];
  division?: string[];
}

/** Promo object type for promotional items in row data */
export interface PromoType {
  description: string | null;
  images: {
    baseImage16X9: string | null;
    baseImage16X5: string | null;
  } | null;
  title: string;
  endDate: number | null;
  startDate: number | null;
  tag?: {
    id: number;
    key: string;
    type?: {
      name: string;
    };
  } | null;
}

/** Row data item from kitchen sink API response */
export interface RowDataItem {
  id: string;
  promo: PromoType | null;
  video: AutoPlayObjectType['video'] | null;
  featured: boolean;
  deeplink: string | null;
  order: number;
}

export interface TypeIdKeys {
  type: string;
  id: string;
}

export interface Video {
  id: string;
  network?: ChannelKey;
  authTypes?: Provider[];
}

export interface GameTeam {
  id?: string;
  name?: string;
  nickName?: string;
  score?: string;
}

export interface Game {
  id?: string;
  statusDesc?: string;
  scheduledTime?: string | number;
  leagueName?: string | null;
  typeDetail?: string;
  leagueId?: string;
  awayTeam: GameTeam;
  homeTeam: GameTeam;
}

export interface PromoItem {
  id: string;
  type: 'PROMO';
  title: string;
  tagKey: string;
  tagType: string;
  trackingId?: string;
  authTypes?: Provider[];
  images?: {
    baseImage16X5?: string | null;
    baseImage16X9?: string | null;
  };
}

export type HttpPayloadBaseType = boolean | string | number | undefined | null;
export type HttpPayloadBaseObject = Record<
  string,
  HttpPayloadBaseType | HttpPayloadBaseType | HttpPayloadBaseType[]
>;
export type HttpPayloadObject = Record<
  string,
  HttpPayloadBaseType | HttpPayloadBaseObject
>;

export interface GridItem {
  id: string;
  title: string;
  name: string;
}

export interface ScoringFixtureTeam {
  mediumName: string;
  nickName: string;
}

export interface ScoringFixtureGame {
  gameStatusDesc: string;
  gameId: string;
  awayTeamId: string;
  awayTeam: ScoringFixtureTeam;
  awayTeamScore: string;
  homeTeamId: string;
  homeTeam: ScoringFixtureTeam;
  homeTeamScore: string;
  scheduledTime: string;
  leagueId: string;
  leagueName: string | null;
  gameTypeDetail?: string;
}

export interface ParsedScoringTeam {
  id: string;
  name: string;
  nickName: string;
  score: string;
}

export interface ParsedScoringGame {
  statusDesc: string;
  id: string;
  awayTeam: ParsedScoringTeam;
  homeTeam: ParsedScoringTeam;
  scheduledTime: string;
  leagueId: string;
  leagueName: string;
  typeDetail: string;
}

export interface ApiError {
  isConnectionError: () => boolean;
  statusCode?: number;
  response?: {
    status?: number;
  };
}

/** Raw kitchen sink fixture module param */
export interface KitchenSinkFixtureModuleParam {
  value: string;
  name: string;
}

/** Raw kitchen sink fixture module */
export interface KitchenSinkFixtureModule {
  params: KitchenSinkFixtureModuleParam[];
  name: string;
  category: string;
  enabled: boolean;
}

/** Raw kitchen sink fixture order item */
export interface KitchenSinkFixtureOrderItem {
  type: string;
  slug: string | null;
  id: string;
  title: string;
  description: string | null;
  url: string;
  layout: string;
  token: string;
}

/** Raw kitchen sink fixture row item - loose typing for complex nested structure */
export interface KitchenSinkFixtureRowItem {
  promo: PromoType | null;
  featured: boolean;
  id: string;
  video: Record<string, unknown>;
  order?: number;
  deeplink: string | null;
}

/** Raw kitchen sink fixture config */
export interface KitchenSinkFixtureConfig {
  modules: KitchenSinkFixtureModule[];
  classes: string[];
}

/** Raw kitchen sink fixture page data */
export interface KitchenSinkFixturePage {
  version: number;
  menuId: number;
  autoplay: string;
  config: KitchenSinkFixtureConfig;
  order: KitchenSinkFixtureOrderItem[];
  rows: Record<string, KitchenSinkFixtureRowItem[]>;
  providers?: Record<string, unknown>;
  networks?: Record<string, unknown>;
  settings?: Record<string, unknown>;
  globalSettings?: Record<string, unknown>;
  playback?: Record<string, unknown>;
  hero?: Record<string, unknown>;
  menu?: unknown[];
  tag_rows?: Record<string, unknown>;
  autoplayObject?: Record<string, unknown>;
}

/** Raw kitchen sink fixture - top level */
export interface KitchenSinkFixture {
  page: KitchenSinkFixturePage;
}

export type ErrorLike = {
  stack?: string | null;
  message?: string | null;
  target?: string | null;
  type?: string | null;
}

/** VTT Cue interface for closed captions */
export interface Cue {
  startTime?: number;
  endTime?: number;
  text?: string;
  line?: number | 'auto';
  snapToLines?: boolean;
  track?: {
    textTrackList?: {
      mediaElement?: HTMLMediaElement;
      length: number;
      [index: number]: { mode: string };
    };
  };
  align?: string;
  vertical?: '' | 'rl' | 'lr' | string;
  positionAlign?: 'line-left' | 'center' | 'line-right' | 'auto' | 'start' | 'end' | string;
  position?: number | 'auto';
  size?: number;
}

export interface GridState {
  isAnimating?: boolean;
  top?: number;
  startIndex?: number;
  endIndex?: number;
  isFocusOnRows?: boolean;
  focusId?: string;
  activeIndex?: number;
}

/** Playback error with optional detail structure */
export interface PlaybackError {
  detail?: {
    error?: {
      code?: string;
    };
  };
}

/** Response from Syncbak playback URL API */
export interface SyncbakPlaybackResponse {
  streamUrl: string;
  mvpdLogoUrl: string;
  stationCode: string;
  errorCode?: string;
}
